import { XIcon } from '@phosphor-icons/react';
import { Dialog } from 'radix-ui';
import type { ReactNode } from 'react';
import { useIsDesktop } from '../lib/media';
import { Button } from './Button';
import { cn } from './cn';

export interface SheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  /** Larghezza della finestra su desktop. */
  size?: 'sm' | 'md' | 'lg';
}

/** Foglio dal basso sul telefono, finestra centrale su desktop (spec 6.3). */
export function Sheet({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  size = 'md',
}: SheetProps) {
  const desktop = useIsDesktop();
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-[var(--scrim)] animate-[fade-in_160ms_ease-out]" />
        <Dialog.Content
          aria-describedby={description ? undefined : undefined}
          className={cn(
            'fixed z-50 flex flex-col bg-sheet text-ink shadow-[var(--shadow-lift)] focus:outline-none',
            desktop
              ? cn(
                  'top-1/2 left-1/2 max-h-[min(86dvh,860px)] w-[calc(100vw-48px)] -translate-x-1/2 -translate-y-1/2 rounded-[var(--radius-sheet)] animate-[pop-in_180ms_ease-out]',
                  size === 'sm' && 'max-w-md',
                  size === 'md' && 'max-w-xl',
                  size === 'lg' && 'max-w-3xl',
                )
              : 'inset-x-0 bottom-0 max-h-[92dvh] rounded-t-[var(--radius-sheet)] animate-[sheet-up_260ms_cubic-bezier(0.2,0.8,0.2,1)]',
          )}
        >
          {!desktop ? (
            <div aria-hidden className="mx-auto mt-2.5 h-1.5 w-10 shrink-0 rounded-full bg-line" />
          ) : null}
          <div className="flex items-start justify-between gap-4 px-5 pt-4 pb-2 md:px-6 md:pt-5">
            <div className="min-w-0">
              <Dialog.Title className="t-title text-xl text-ink">{title}</Dialog.Title>
              {description ? (
                <Dialog.Description className="mt-1 text-[0.9375rem] text-ink-2">
                  {description}
                </Dialog.Description>
              ) : (
                <Dialog.Description className="sr-only">{title}</Dialog.Description>
              )}
            </div>
            <Dialog.Close
              aria-label="Chiudi"
              className="-mt-1 -mr-2 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-ink-2 hover:bg-ink/6"
            >
              <XIcon size={20} aria-hidden />
            </Dialog.Close>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pb-5 md:px-6">
            {children}
          </div>
          {footer ? (
            <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-line-2 px-5 pt-3 pb-[max(12px,env(safe-area-inset-bottom))] md:flex-row md:justify-end md:px-6 md:pb-4">
              {footer}
            </div>
          ) : (
            <div className="shrink-0 pb-[env(safe-area-inset-bottom)]" />
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export interface ConfirmProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  message: string;
  confirmLabel: string;
  danger?: boolean;
  loading?: boolean;
  onConfirm: () => void;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  message,
  confirmLabel,
  danger,
  loading,
  onConfirm,
}: ConfirmProps) {
  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Annulla
          </Button>
          <Button variant={danger ? 'danger' : 'primary'} loading={loading} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p className="text-[0.9375rem] text-ink-2">{message}</p>
    </Sheet>
  );
}
